import { useState, useEffect } from 'react';
import { Save, BarChart3, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { adminApi } from '@/lib/api';

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    site_name: '', site_tagline: '', phone: '', email: '', address: '',
    facebook_url: '', twitter_url: '', linkedin_url: '', instagram_url: '',
    about_text: '', copyright_text: '', scheduling_url: '',
    ga_tracking_id: '', ga_enabled: false, gtm_container_id: '', fb_pixel_id: ''
  });

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try { const res = await adminApi.getSiteSettings(); setSettings(res.data); }
    catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await adminApi.updateSiteSettings(settings); toast.success('Settings saved!'); }
    catch (error) { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-3xl font-heading font-bold">Site Settings</h1><p className="text-muted-foreground">Global website configuration</p></div>
        <Button onClick={handleSubmit} disabled={saving} data-testid="save-settings-btn"><Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Settings'}</Button>
      </div>

      <form onSubmit={handleSubmit} className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>General</CardTitle><CardDescription>Basic site information</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2"><Label>Site Name</Label><Input value={settings.site_name} onChange={(e) => setSettings(p => ({ ...p, site_name: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Tagline</Label><Input value={settings.site_tagline} onChange={(e) => setSettings(p => ({ ...p, site_tagline: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Copyright Text</Label><Input value={settings.copyright_text} onChange={(e) => setSettings(p => ({ ...p, copyright_text: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Scheduling URL</Label><Input value={settings.scheduling_url} onChange={(e) => setSettings(p => ({ ...p, scheduling_url: e.target.value }))} /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Contact</CardTitle><CardDescription>Contact information</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2"><Label>Phone</Label><Input value={settings.phone} onChange={(e) => setSettings(p => ({ ...p, phone: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={settings.email} onChange={(e) => setSettings(p => ({ ...p, email: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Address</Label><Textarea value={settings.address} onChange={(e) => setSettings(p => ({ ...p, address: e.target.value }))} rows={2} /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Social Media</CardTitle><CardDescription>Social profile links</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2"><Label>Facebook URL</Label><Input value={settings.facebook_url} onChange={(e) => setSettings(p => ({ ...p, facebook_url: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Twitter URL</Label><Input value={settings.twitter_url} onChange={(e) => setSettings(p => ({ ...p, twitter_url: e.target.value }))} /></div>
            <div className="space-y-2"><Label>LinkedIn URL</Label><Input value={settings.linkedin_url} onChange={(e) => setSettings(p => ({ ...p, linkedin_url: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Instagram URL</Label><Input value={settings.instagram_url} onChange={(e) => setSettings(p => ({ ...p, instagram_url: e.target.value }))} /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>About</CardTitle><CardDescription>Footer about text</CardDescription></CardHeader>
          <CardContent><div className="space-y-2"><Label>About Text</Label><Textarea value={settings.about_text} onChange={(e) => setSettings(p => ({ ...p, about_text: e.target.value }))} rows={5} /></div></CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Analytics & Tracking
                </CardTitle>
                <CardDescription>Configure Google Analytics, Tag Manager, and Facebook Pixel</CardDescription>
              </div>
              {settings.ga_enabled && settings.ga_tracking_id && (
                <Badge className="bg-green-500">Active</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Enable Google Analytics</Label>
                    <p className="text-sm text-muted-foreground">Track page views, user behavior, and conversions</p>
                  </div>
                  <Switch 
                    checked={settings.ga_enabled} 
                    onCheckedChange={(checked) => setSettings(p => ({ ...p, ga_enabled: checked }))} 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Google Analytics Tracking ID</Label>
                  <Input 
                    value={settings.ga_tracking_id} 
                    onChange={(e) => setSettings(p => ({ ...p, ga_tracking_id: e.target.value }))} 
                    placeholder="G-XXXXXXXXXX or UA-XXXXXXXXX-X"
                  />
                  <p className="text-xs text-muted-foreground">
                    Find your ID in{' '}
                    <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                      Google Analytics <ExternalLink className="w-3 h-3" />
                    </a>
                    {' '}→ Admin → Data Streams → Your Stream
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Google Tag Manager Container ID</Label>
                  <Input 
                    value={settings.gtm_container_id} 
                    onChange={(e) => setSettings(p => ({ ...p, gtm_container_id: e.target.value }))} 
                    placeholder="GTM-XXXXXXX"
                  />
                  <p className="text-xs text-muted-foreground">Optional: For advanced tag management</p>
                </div>
                <div className="space-y-2">
                  <Label>Facebook Pixel ID</Label>
                  <Input 
                    value={settings.fb_pixel_id} 
                    onChange={(e) => setSettings(p => ({ ...p, fb_pixel_id: e.target.value }))} 
                    placeholder="XXXXXXXXXXXXXXX"
                  />
                  <p className="text-xs text-muted-foreground">Optional: For Facebook/Meta ad tracking</p>
                </div>
              </div>
            </div>

            {settings.ga_enabled && settings.ga_tracking_id && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>Google Analytics is active!</strong> Page views and events are being tracked. 
                  View your data in the{' '}
                  <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="underline">
                    Google Analytics dashboard
                  </a>.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default AdminSettings;
